import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { HubConnection, HubConnectionBuilder, HubConnectionState, HttpTransportType, IRetryPolicy } from '@microsoft/signalr';
import { getAccessToken } from '../utils/request';

// Retry policy that never gives up — keeps retrying so a long server cold-start
// (e.g. an Azure App Service slot swap / >47s blip) doesn't exhaust the budget and
// trigger onclose. Caps the backoff at 30s; returning a number (never null) => retry forever.
const foreverRetryPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds(ctx) {
    const schedule = [0, 2000, 5000, 10000, 30000];
    return schedule[Math.min(ctx.previousRetryCount, schedule.length - 1)];
  },
};

// Singleton connection instance
let sharedConnection: HubConnection | null = null;
let connectionPromise: Promise<HubConnection> | null = null;
let connectionRefCount = 0;

// Event handlers registry - allows multiple handlers per event
type EventHandler = (data: any) => void;
const eventHandlers: Map<string, Set<EventHandler>> = new Map();

// Track group subscriptions
const subscribedGroups: Set<string> = new Set();

// Known events that the hub can send
const KNOWN_EVENTS = [
  'Message',
  'CompanyRulesUpdated',
  'ModuleConfigUpdated',
  'FaceRecognitionSettingsUpdated',
  'UserClocks',
  'UsersAvailability',
  'MigrationProgress',
  'MigrationComplete',
  'LeaveApproved',
  'LeaveRejected',
  'LeaveUndone',
  'LeaveCancelled',
  'OtpReceived',
  'NotificationReceived',
  'TournamentStatusChanged',
  'TournamentListChanged',
  'MatchScoreUpdated',
  'MatchStatusChanged',
  'MatchCreated',
  'MatchUpdated',
  'MatchDeleted',
  'RefereeAssignedToMatch',
  'RefereeUnassignedFromMatch',
  'AssignmentResponseChanged',
  'LiveScoreUpdated',
];

// Single pending revival timer for the onclose recovery path — one at a time,
// re-armed with backoff on failure, cleared when the last component unmounts.
let revivalTimer: ReturnType<typeof setTimeout> | null = null;

const scheduleRevival = (delayMs: number) => {
  if (revivalTimer) return; // never stack timers — one pending revival at a time
  revivalTimer = setTimeout(() => {
    revivalTimer = null;
    // Re-check the guards at fire time — components may have unmounted or the
    // user logged out while the timer was pending.
    if (connectionRefCount > 0 && getAccessToken()) {
      getOrCreateConnection().catch(() => {
        // Still down — re-arm with modest backoff (2s → 5s → 12.5s → 30s cap) so
        // an idle page eventually recovers instead of staying dead forever.
        scheduleRevival(Math.min(delayMs * 2.5, 30000));
      });
    }
  }, delayMs);
};

// Wait for an existing connection that is Connecting/Reconnecting to reach
// Connected, instead of building a SECOND connection alongside it. Rejects if
// the connection dies or is torn down/replaced while waiting, so callers'
// retry loops fall through to a fresh getOrCreateConnection() attempt.
const waitForConnected = (conn: HubConnection): Promise<HubConnection> =>
  new Promise((resolve, reject) => {
    const check = () => {
      if (sharedConnection !== conn) {
        reject(new Error('SignalR: connection was replaced or torn down while waiting'));
        return;
      }
      if (conn.state === HubConnectionState.Connected) {
        resolve(conn);
        return;
      }
      if (conn.state === HubConnectionState.Disconnected) {
        reject(new Error('SignalR: connection closed while waiting to connect'));
        return;
      }
      setTimeout(check, 500);
    };
    check();
  });

/**
 * Gets or creates a shared SignalR connection.
 * Uses reference counting to manage the connection lifecycle.
 * Requires a valid access token — will reject if none is available.
 * Invariant: AT MOST ONE HubConnection ever exists from this module — an
 * existing connection mid-(re)connect is awaited, never duplicated.
 */
const getOrCreateConnection = async (): Promise<HubConnection> => {
  if (sharedConnection && sharedConnection.state === HubConnectionState.Connected) {
    return sharedConnection;
  }

  if (connectionPromise) {
    return connectionPromise;
  }

  // An existing connection that is Connecting or Reconnecting is "in progress" —
  // foreverRetryPolicy keeps it Reconnecting for the whole slot-swap gap (onclose
  // never fires), so building a new connection here would leave TWO live
  // connections when the server returns (duplicate events, orphan clobbering
  // globals). Wait for the existing one instead.
  if (sharedConnection && (
    sharedConnection.state === HubConnectionState.Connecting ||
    sharedConnection.state === HubConnectionState.Reconnecting
  )) {
    return waitForConnected(sharedConnection);
  }

  // Guard: require a valid token before attempting to connect
  const token = getAccessToken();
  if (!token) {
    throw new Error('SignalR: No access token available — connection deferred');
  }

  connectionPromise = (async () => {
    const connection = new HubConnectionBuilder()
      .withUrl('/api/mainhub', {
        accessTokenFactory: () => getAccessToken() || '',
        // Prefer WebSockets, fall back to SSE then LongPolling
        transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect(foreverRetryPolicy)
      .build();

    // Set up event forwarding for all known events
    KNOWN_EVENTS.forEach(eventName => {
      connection.on(eventName, (data: any) => {
        const handlers = eventHandlers.get(eventName);
        if (handlers) {
          handlers.forEach(handler => {
            try {
              handler(data);
            } catch (err) {
              console.error(`Error in ${eventName} handler:`, err);
            }
          });
        }
      });
    });

    // Re-subscribe to all tracked groups on a given connection. Runs both on
    // onreconnected (same connection revived) AND after a fresh start() (rebuilt
    // connection) so a cold-start-rebuilt connection rejoins every group.
    // Safe to repeat: server Groups.AddToGroupAsync is idempotent + client dedupes.
    const replaySubscriptions = async (conn: HubConnection) => {
      for (const group of subscribedGroups) {
        try {
          // Split on the FIRST ':' only — the args JSON may itself contain ':'
          // (e.g. string args), so a naive split(':') would truncate it.
          const sep = group.indexOf(':');
          await conn.invoke(group.slice(0, sep), ...JSON.parse(group.slice(sep + 1) || '[]'));
        } catch (err: unknown) {
          const errMsg = err instanceof Error ? err.message : String(err);
          console.error(`Failed to re-subscribe to ${group}:`, err);
          if (errMsg.includes('401') || errMsg.includes('Unauthorized')) {
            // Token expired — close connection to force fresh reconnect
            conn.stop();
            break;
          }
        }
      }
    };

    // Handle reconnection - re-subscribe to all groups
    connection.onreconnected(() => replaySubscriptions(connection));

    connection.onclose((error) => {
      if (error) {
        console.error('SignalR connection closed with error:', error.message);
      }
      // Identity guard: only the connection that OWNS the globals may mutate them.
      // A stale/orphaned connection closing must never null the live connection's
      // globals out from under it (or trigger a competing revival).
      if (sharedConnection !== connection) {
        return;
      }
      sharedConnection = null;
      connectionPromise = null;
      // Revive only if components still need it AND the user is still authenticated —
      // must NOT fight the intentional teardown (ref-count → 0) or the 401 stop() path.
      // The 2s delay + refCount/auth guards prevent a runaway restart loop after logout;
      // scheduleRevival re-arms itself with backoff if the attempt fails (idle-page recovery).
      if (connectionRefCount > 0 && getAccessToken()) {
        scheduleRevival(2000);
      }
    });

    try {
      await connection.start();
    } catch (err) {
      // Initial connect failed (e.g. a negotiate 503/timeout during a server cold-start
      // or slot-swap window, or a transient service blip). Clear the cached promise so the
      // NEXT getOrCreateConnection() retries a fresh attempt instead of returning this
      // rejected promise forever (which would leave real-time dead until a page reload —
      // the initial-connect analog of the reconnect bug foreverRetryPolicy fixes).
      connectionPromise = null;
      throw err;
    }
    sharedConnection = connection;
    connectionPromise = null;
    // Rejoin all tracked groups on this freshly-started connection (covers a
    // rebuilt-from-scratch connection after a cold start, where onreconnected never fires).
    await replaySubscriptions(connection);
    return connection;
  })();

  return connectionPromise;
};

/**
 * Invoke a hub method with the shared connection.
 */
const invokeHubMethod = async (methodName: string, ...args: any[]): Promise<any> => {
  const connection = await getOrCreateConnection();
  if (connection.state === HubConnectionState.Connected) {
    return connection.invoke(methodName, ...args);
  }
  throw new Error('SignalR connection not connected');
};

/**
 * Subscribe to a group (tracks subscription for reconnection).
 */
const subscribeToGroup = async (methodName: string, ...args: any[]): Promise<void> => {
  const groupKey = `${methodName}:${JSON.stringify(args)}`;
  if (subscribedGroups.has(groupKey)) {
    return;
  }

  await invokeHubMethod(methodName, ...args);
  subscribedGroups.add(groupKey);
};

/**
 * Hook that provides a shared SignalR connection.
 * Automatically manages connection lifecycle with reference counting.
 */
export const useSignalRConnection = () => {
  const connectionRef = useRef<HubConnection | null>(null);
  const isLoggedIn = useSelector((state: any) => state.user?.isLoggedIn);

  // Subscribe to an event
  const subscribe = useCallback((event: string, handler: EventHandler) => {
    if (!eventHandlers.has(event)) {
      eventHandlers.set(event, new Set());
    }
    eventHandlers.get(event)!.add(handler);

    // Return unsubscribe function
    return () => {
      eventHandlers.get(event)?.delete(handler);
    };
  }, []);

  // Subscribe to company group
  const subscribeToCompanyGroup = useCallback(async (companyId: number) => {
    await subscribeToGroup('SubscribeToCompanyGroup', companyId);
  }, []);

  // Subscribe to face recognition settings
  const subscribeToFaceRecognitionSettings = useCallback(async () => {
    await subscribeToGroup('SubscribeToFaceRecognitionSettings');
  }, []);

  // Subscribe to personal attendance pool
  const subscribeToPersonalAttendancePool = useCallback(async (userId: string) => {
    await subscribeToGroup('SubscribeToPersonalAttendancePool', userId);
  }, []);

  // Subscribe to attendance pool (for dashboard)
  const subscribeToAttendancePool = useCallback(async () => {
    await subscribeToGroup('SubscribeToAttendancePool');
  }, []);

  // Subscribe to tournament group
  const subscribeToTournamentGroup = useCallback(async (tournamentId: number) => {
    await subscribeToGroup('SubscribeToTournamentGroup', tournamentId);
  }, []);

  // Unsubscribe from tournament group
  const unsubscribeFromTournamentGroup = useCallback(async (tournamentId: number) => {
    const groupKey = `SubscribeToTournamentGroup:${JSON.stringify([tournamentId])}`;
    subscribedGroups.delete(groupKey);
    try {
      await invokeHubMethod('UnsubscribeFromTournamentGroup', tournamentId);
    } catch (err) {
      console.error('Failed to unsubscribe from tournament group:', err);
    }
  }, []);

  // Generic invoke method
  const invoke = useCallback(async (methodName: string, ...args: any[]) => {
    return invokeHubMethod(methodName, ...args);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;

    let mounted = true;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    connectionRefCount++;

    const setup = async () => {
      try {
        const connection = await getOrCreateConnection();
        if (mounted) {
          connectionRef.current = connection;
        }
      } catch (err) {
        // Retry on failure so a transient error recovers WITHOUT a page reload:
        // token-not-ready retries fast (2s); a real connect failure (negotiate error
        // during a cold-start/swap, or a degraded/at-capacity server) backs off to 5s
        // so it keeps trying at a sane rate rather than tight-looping. Concurrent
        // retries from multiple mounted hooks collapse onto the shared connectionPromise.
        const msg = err instanceof Error ? err.message : '';
        if (mounted) {
          const tokenNotReady = msg.includes('No access token available');
          if (!tokenNotReady) {
            console.error('Failed to establish SignalR connection (will retry):', err);
          }
          retryTimeout = setTimeout(setup, tokenNotReady ? 2000 : 5000);
        }
      }
    };

    setup();

    return () => {
      mounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
      connectionRefCount--;

      // Only close connection when no more components are using it
      if (connectionRefCount === 0) {
        // Cancel any pending onclose revival — nothing needs the connection anymore.
        if (revivalTimer) {
          clearTimeout(revivalTimer);
          revivalTimer = null;
        }
        if (sharedConnection) {
          sharedConnection.stop().catch(() => {});
          sharedConnection = null;
          subscribedGroups.clear();
          eventHandlers.clear();
        }
      }
    };
  }, [isLoggedIn]);

  return {
    connection: connectionRef.current,
    subscribe,
    subscribeToCompanyGroup,
    subscribeToFaceRecognitionSettings,
    subscribeToPersonalAttendancePool,
    subscribeToAttendancePool,
    subscribeToTournamentGroup,
    unsubscribeFromTournamentGroup,
    invoke,
    isConnected: sharedConnection?.state === HubConnectionState.Connected,
  };
};

export default useSignalRConnection;
